/**
 * useStatusTracking - Centralized Status Event Tracking for AI Assistant
 *
 * This composable provides session-aware status tracking for Amplitude events.
 * It handles deduplication to ensure events are only tracked once per session.
 *
 * Key features:
 * - Session-aware deduplication (clears when session changes)
 * - Maps NOA recorder statuses to Amplitude event names
 * - Can be called from both WebSocket and Polling flows
 */

import { UserTracking } from "@/utils/UserTracking";
import { identifyEnvironment } from "@/utils/IdentifyGlobalEnv";

// =============================================================================
// Module-Level State (Singleton)
// =============================================================================

/** Set of status events already emitted for current session */
const emittedEventsForSession = new Set<string>();

/** Current session ID for deduplication tracking */
let currentSessionId: string | null = null;

// =============================================================================
// Status to Event Mapping
// =============================================================================

/**
 * Map of NOA recorder statuses to their corresponding Amplitude tracking functions.
 * Each function tracks the appropriate event with required properties.
 */
const STATUS_TRACKING_MAP: Record<string, () => void> = {
  interrupted: () =>
    UserTracking.trackEvent("recording_status_changed", {
      status: "interrupted",
      page_category: "noa notes",
      page_section: "",
      page_url: window.location.href,
    }),
};

// =============================================================================
// Main Composable
// =============================================================================

export function useStatusTracking() {
  /**
   * Track a status change event for analytics.
   * Handles session-aware deduplication to prevent duplicate events.
   *
   * @param status - The NOA recorder status (e.g., "recording", "recorded", "summarized")
   * @param sessionId - The current session ID for deduplication
   */
  function trackStatus(status: string, sessionId: string | null): void {
    // Clear tracking when session changes
    if (currentSessionId !== sessionId) {
      currentSessionId = sessionId;
      emittedEventsForSession.clear();
    }

    const normalizedStatus = status.toLowerCase();

    // Skip if already tracked for this session
    if (emittedEventsForSession.has(normalizedStatus)) {
      if (identifyEnvironment() === "local" || identifyEnvironment() === "staging") {
        console.log(`[StatusTracking] Skipping duplicate event '${normalizedStatus}' for session ${sessionId}`);
      }
      return;
    }

    // Execute tracking if status has a mapping
    const trackingFn = STATUS_TRACKING_MAP[normalizedStatus];
    if (trackingFn) {
      trackingFn();
      emittedEventsForSession.add(normalizedStatus);

      if (identifyEnvironment() === "local" || identifyEnvironment() === "staging") {
        console.log(`[StatusTracking] Tracked event for status '${normalizedStatus}' (session: ${sessionId})`);
      }
    }
  }

  /**
   * Reset tracking state. Call when session ends or is discarded.
   */
  function resetTracking(): void {
    emittedEventsForSession.clear();
    currentSessionId = null;
  }

  /**
   * Get current tracking state (for debugging)
   */
  function getTrackingState(): { sessionId: string | null; emittedEvents: string[] } {
    return {
      sessionId: currentSessionId,
      emittedEvents: Array.from(emittedEventsForSession),
    };
  }

  return {
    trackStatus,
    resetTracking,
    getTrackingState,
  };
}
